import type { RouterOutputs } from "~/utils/api"
import type { DraftChange } from "~/components/prototype/allocation-planning/DraftCommandbar"
import {
  OUTLET_FORMAT,
  STORE_FIELD_LABELS,
  storeFormatLabel,
  type StoreField,
  type StorePatch,
} from "~/utils/allocationStores"

export type StoreValues = Pick<RouterOutputs["allocation"]["getStoreSetup"][number], StoreField>

export type StoreEdits = Partial<StoreValues>

/** Staged edits per store id; a store or field drops out once it matches the saved value again. */
export type StoreDraft = Record<string, StoreEdits>

const STORE_FIELDS = Object.keys(STORE_FIELD_LABELS) as StoreField[]

/** Trimmed text, or null when nothing is left: what a cleared text field saves. */
export function normalizeStoreText(value: unknown): string | null {
  const text = value == null ? "" : String(value).trim()
  return text === "" ? null : text
}

export function stagedStoreValues(saved: StoreValues, edits: StoreEdits | undefined): StoreValues {
  return edits ? { ...saved, ...edits } : saved
}

function withField<F extends StoreField>(
  edits: StoreEdits,
  saved: StoreValues,
  field: F,
  value: StoreValues[F],
): StoreEdits {
  const { [field]: _previous, ...rest } = edits
  return value === saved[field] ? rest : { ...rest, [field]: value }
}

/** Stages one field. Moving a store to the outlet format clears its grade (outlets carry none);
 * moving it off a staged outlet format restores the saved grade. */
export function stageStoreEdit<F extends StoreField>(
  draft: StoreDraft,
  storeId: string,
  saved: StoreValues,
  field: F,
  value: StoreValues[F],
): StoreDraft {
  const wasOutlet = stagedStoreValues(saved, draft[storeId]).format === OUTLET_FORMAT
  let edits = withField(draft[storeId] ?? {}, saved, field, value)
  if (field === "format" && value === OUTLET_FORMAT) edits = withField(edits, saved, "grade", null)
  else if (field === "format" && wasOutlet && saved.format !== OUTLET_FORMAT)
    edits = withField(edits, saved, "grade", saved.grade)
  const rest = Object.fromEntries(Object.entries(draft).filter(([id]) => id !== storeId))
  return Object.keys(edits).length === 0 ? rest : { ...rest, [storeId]: edits }
}

export function storeDraftPatches(draft: StoreDraft): StorePatch[] {
  return Object.entries(draft).map(([storeId, edits]) => ({ storeId, ...edits }))
}

const EMPTY = "—"

export function formatStoreValue<F extends StoreField>(
  field: F,
  value: StoreValues[F] | "",
): string {
  if (value === null || value === "") return EMPTY
  switch (field) {
    case "format":
      return storeFormatLabel(String(value))
    case "sellingArea":
      return `${value} m2`
    case "transitDays":
      return `${value} days`
    default:
      return String(value)
  }
}

export function storeDraftChanges(
  draft: StoreDraft,
  stores: { id: string; name: string; values: StoreValues }[],
): DraftChange[] {
  return stores.flatMap((store) => {
    const edits = draft[store.id]
    if (!edits) return []
    const after = stagedStoreValues(store.values, edits)
    return STORE_FIELDS.filter((field) => field in edits).map((field) => ({
      key: `store:${store.id}:${field}`,
      label: `${store.name} · ${STORE_FIELD_LABELS[field]}`,
      before: formatStoreValue(field, store.values[field]),
      after: formatStoreValue(field, after[field]),
    }))
  })
}
